/**
 * PublicPaymentPage - Page publique de paiement de facture
 * Design System v2 - Style Landing (teal gradient, text-white, lime accents)
 *
 * Route: /pay/:token
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CreditCard,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { logger } from '@/utils/logger';

import {
  usePaymentOptions,
  useInitiateStripePayment,
  useInitiatePayPalPayment,
  useCapturePayPalPayment,
} from '../hooks/usePublicPayment';
import { InvoiceSummary } from './InvoiceSummary';
import { StripePaymentForm } from './StripePaymentForm';
import { PayPalButton } from './PayPalButton';
import type { PaymentMethod, StripePaymentResult } from '../types/payment.types';

export const PublicPaymentPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  // État du paiement
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [stripeData, setStripeData] = useState<StripePaymentResult | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Hooks de données
  const {
    data: optionsData,
    isLoading: isLoadingOptions,
    error: optionsError,
  } = usePaymentOptions(token);

  const initiateStripe = useInitiateStripePayment();
  const initiatePayPal = useInitiatePayPalPayment();
  const capturePayPal = useCapturePayPalPayment();

  // Gérer le retour PayPal
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const orderId = searchParams.get('orderId');

    if (paymentStatus === 'success' && token) {
      setPaymentSuccess(true);
      notify.success('Paiement effectué avec succès !', 'Paiement');
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      notify.warning('Paiement annulé', 'Paiement');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (orderId && token) {
      // Capturer le paiement PayPal
      handlePayPalCapture(orderId);
    }
  }, [searchParams, token]);

  // Capturer le paiement PayPal après retour
  const handlePayPalCapture = async (orderId: string) => {
    if (!token) return;

    try {
      const result = await capturePayPal.mutateAsync({ token, orderId });
      if (result.data.success) {
        setPaymentSuccess(true);
        notify.success('Paiement PayPal effectué avec succès !', 'Paiement');
      } else {
        throw new Error('La capture du paiement a échoué');
      }
    } catch (err) {
      logger.error('PayPal capture error:', err);
      setPaymentError((err as Error).message);
      notify.error((err as Error).message, 'Erreur');
    }
    // Nettoyer l'URL
    window.history.replaceState({}, '', window.location.pathname);
  };

  // Initier le paiement Stripe
  const handleStripeSelect = async () => {
    if (!token) return;

    setSelectedMethod('stripe');
    setPaymentError(null);

    try {
      const result = await initiateStripe.mutateAsync(token);
      setStripeData(result.data);
    } catch (err) {
      logger.error('Stripe init error:', err);
      setPaymentError((err as Error).message);
      setSelectedMethod(null);
    }
  };

  // Initier le paiement PayPal
  const handlePayPalPayment = async () => {
    if (!token) return;

    setSelectedMethod('paypal');
    setPaymentError(null);

    const returnUrl = `${window.location.origin}${window.location.pathname}?payment=success`;
    const cancelUrl = `${window.location.origin}${window.location.pathname}?payment=cancelled`;

    try {
      const result = await initiatePayPal.mutateAsync({
        token,
        returnUrl,
        cancelUrl,
      });
      // Rediriger vers PayPal
      window.location.href = result.data.approvalUrl;
    } catch (err) {
      logger.error('PayPal init error:', err);
      setPaymentError((err as Error).message);
      setSelectedMethod(null);
    }
  };

  // Formater la devise
  const formatCurrency = (amount: number, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  // Extraire les données
  const paymentOptions = optionsData?.data;
  const invoice = paymentOptions?.invoice;
  const methods = paymentOptions?.paymentMethods;

  // Calculer le montant à payer
  const amountToPay = (invoice?.remainingAmount ?? 0) > 0
    ? (invoice?.remainingAmount ?? 0)
    : (invoice?.totalTTC ?? 0);

  // États de chargement et erreur
  if (isLoadingOptions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-lime-400 animate-spin mx-auto mb-4" />
          <p className="text-white/80">{t('publicPayment.loading')}</p>
        </div>
      </div>
    );
  }

  if (optionsError || !paymentOptions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('publicPayment.invalidLink')}</h1>
          <p className="text-white/60">
            {(optionsError as Error)?.message || t('publicPayment.invalidLinkMessage')}
          </p>
        </div>
      </div>
    );
  }

  // Facture déjà payée
  if (invoice?.status === 'PAID' || paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-lime-500/20 flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-10 h-10 text-lime-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">{t('publicPayment.paymentSuccess')}</h1>
          <p className="text-white/60 mb-6">
            {t('publicPayment.paymentSuccessMessage')}
          </p>
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
            <p className="text-white/60 text-sm">{t('publicPayment.invoiceLabel')}</p>
            <p className="text-white font-bold text-lg">{invoice?.number}</p>
            <p className="text-lime-400 font-bold text-xl mt-2">
              {formatCurrency(amountToPay, invoice?.currencyCode)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Aucune méthode de paiement configurée
  const hasPaymentMethods = methods?.stripe?.enabled || methods?.paypal?.enabled;
  if (!hasPaymentMethods) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('publicPayment.paymentNotAvailable')}</h1>
          <p className="text-white/60">
            {t('publicPayment.paymentNotAvailableMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,255,0,0.1),transparent_50%)]" />

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8 md:py-12">
        {/* En-tête */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-4">
            <Shield className="w-4 h-4 text-lime-400" />
            <span className="text-white/80 text-sm">{t('publicPayment.securePayment')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t('publicPayment.payYourInvoice')}
          </h1>
          <p className="text-white/60">
            {t('publicPayment.choosePaymentMethod')}
          </p>
        </div>

        {/* Résumé de la facture */}
        <div className="mb-8">
          <InvoiceSummary invoice={invoice!} formatCurrency={formatCurrency} />
        </div>

        {/* Erreur de paiement */}
        {paymentError && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-red-500/20 rounded-xl border border-red-400/30">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-200">{paymentError}</p>
          </div>
        )}

        {/* Sélection de la méthode de paiement */}
        {!selectedMethod && (
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 space-y-4">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-lime-400" />
              {t('publicPayment.chooseMethod')}
            </h2>

            {/* Bouton Stripe */}
            {methods?.stripe?.enabled && (
              <button
                onClick={handleStripeSelect}
                disabled={initiateStripe.isPending}
                className="w-full py-4 bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 font-bold rounded-xl
                  hover:shadow-lg hover:shadow-lime-400/25 hover:-translate-y-0.5
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-300 flex items-center justify-center gap-3"
              >
                {initiateStripe.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('publicPayment.preparing')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    {t('publicPayment.payByCard')}
                  </>
                )}
              </button>
            )}

            {/* Séparateur */}
            {methods?.stripe?.enabled && methods?.paypal?.enabled && (
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-white/20" />
                <span className="text-white/40 text-sm">{t('publicPayment.or')}</span>
                <div className="flex-1 h-px bg-white/20" />
              </div>
            )}

            {/* Bouton PayPal */}
            {methods?.paypal?.enabled && (
              <PayPalButton
                isLoading={initiatePayPal.isPending}
                disabled={initiateStripe.isPending}
                onClick={handlePayPalPayment}
                amount={amountToPay}
                currency={invoice?.currencyCode || 'EUR'}
              />
            )}
          </div>
        )}

        {/* Formulaire Stripe */}
        {selectedMethod === 'stripe' && stripeData && (
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-lime-400" />
                {t('publicPayment.cardPayment')}
              </h2>
              <button
                onClick={() => {
                  setSelectedMethod(null);
                  setStripeData(null);
                }}
                className="text-white/60 hover:text-white text-sm flex items-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('publicPayment.back')}
              </button>
            </div>

            <StripePaymentForm
              clientSecret={stripeData.clientSecret}
              publishableKey={stripeData.publishableKey}
              amount={stripeData.amount}
              currency={stripeData.currency}
              onSuccess={() => setPaymentSuccess(true)}
              onError={(error) => setPaymentError(error)}
            />
          </div>
        )}

        {/* Footer sécurité */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-white/40 text-xs">
            <Lock className="w-3 h-3" />
            <span>{t('publicPayment.dataEncrypted')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentPage;
